import { useState, useRef, useEffect } from 'react'
import { Bell } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useNotifications } from '@/hooks/useNotifications'
import { useDoNotContact } from '@/hooks/useDoNotContact'
import type { Notification, NotificationEventType } from '@/types/api'

const EVENT_ICONS: Record<NotificationEventType, string> = {
  reply_received: '📩',
  bounce: '⚠️',
  sequence_completed: '✅',
  enrollment_started: '🚀',
  enrollment_failed: '❌',
  account_health_pause: '🚫',
  account_health_warning: '⚠️',
  account_health_recovered: '✅',
  sequence_no_sender: '⛔',
  hubspot_data_purged: '🧹',
  hubspot_retention_skipped: '🛑',
  feedback_in_progress: '🔧',
  feedback_resolved: '✅',
  feedback_not_planned: '📋',
  feedback_team_reply: '💬',
}

// A notification links no report: its type sends the author to their reports.
const isFeedbackEvent = (eventType: NotificationEventType): boolean =>
  eventType.startsWith('feedback_')

function timeAgo(dateString: string, t: TFunction<'common'>): string {
  const seconds = Math.floor(
    (Date.now() - new Date(dateString).getTime()) / 1000
  )
  if (seconds < 60) return t('notifications.timeAgo.now')
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return t('notifications.timeAgo.minutes', { count: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('notifications.timeAgo.hours', { count: hours })
  const days = Math.floor(hours / 24)
  return t('notifications.timeAgo.days', { count: days })
}

function NotificationItem({
  notification,
  onRead,
}: {
  notification: Notification
  onRead: (id: string) => void
}) {
  const navigate = useNavigate()
  const { t } = useTranslation('common')

  const handleClick = () => {
    onRead(notification.id)
    if (notification.enrollment_id) {
      navigate(`/enrollments?id=${notification.enrollment_id}`)
    } else if (notification.sequence_id) {
      navigate(`/sequences/${notification.sequence_id}`)
    } else if (isFeedbackEvent(notification.event_type)) {
      navigate('/feedback')
    }
  }

  return (
    <div
      className={`border-b border-gray-100 ${
        !notification.is_read ? 'bg-indigo-50/50' : ''
      }`}
    >
      <button
        onClick={handleClick}
        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            <span className="text-sm mt-0.5 shrink-0">
              {EVENT_ICONS[notification.event_type]}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {notification.title}
              </p>
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                {notification.message}
              </p>
            </div>
          </div>
          <span className="text-[11px] text-gray-400 whitespace-nowrap shrink-0">
            {timeAgo(notification.created_at, t)}
          </span>
        </div>
      </button>
      {/* Only when the API says the contact is there to be marked: a deleted
          contact, or an API that does not know the mark yet, says nothing */}
      {notification.event_type === 'reply_received' &&
        notification.contact_id &&
        typeof notification.contact_do_not_contact === 'boolean' && (
        <DoNotContactAction
          contactId={notification.contact_id}
          marked={notification.contact_do_not_contact}
        />
      )}
    </div>
  )
}

// Who answers "do not write to me again" is marked from the notification of
// that reply (#372). A second click confirms: the row is not a place for a
// dialog, and the mark stops every sequence of the contact.
function DoNotContactAction({
  contactId,
  marked,
}: {
  contactId: string
  marked: boolean
}) {
  const { t } = useTranslation('common')
  const doNotContact = useDoNotContact()
  const [confirming, setConfirming] = useState(false)

  const handleConfirm = () => {
    const done = () => setConfirming(false)
    if (marked) {
      doNotContact.unmark(contactId, done)
    } else {
      doNotContact.mark(contactId, done)
    }
  }

  if (confirming) {
    return (
      <div className="px-4 pb-3 pl-10 text-xs text-gray-600">
        <p>
          {marked
            ? t('notifications.doNotContact.removeConfirm')
            : t('notifications.doNotContact.markConfirm')}
        </p>
        <div className="mt-1 flex gap-3">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={doNotContact.isPending}
            className="font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
          >
            {t('notifications.doNotContact.confirm')}
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            disabled={doNotContact.isPending}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            {t('cancel')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 pb-3 pl-10 text-xs flex items-center gap-2">
      {marked && (
        <span className="text-gray-500">{t('notifications.doNotContact.marked')}</span>
      )}
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className={
          marked
            ? 'text-indigo-600 hover:text-indigo-800'
            : 'text-red-600 hover:text-red-800'
        }
      >
        {marked
          ? t('notifications.doNotContact.remove')
          : t('notifications.doNotContact.mark')}
      </button>
    </div>
  )
}

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation('common')
  const { notifications, unreadCount, markRead, markAllRead, refetch } =
    useNotifications()

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleToggle = () => {
    if (!isOpen) {
      refetch()
    }
    setIsOpen(!isOpen)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors relative"
        title={t('notifications.title')}
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold min-w-[16px] h-4 flex items-center justify-center rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[400px] flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <span className="text-sm font-semibold text-gray-900">
              {t('notifications.title')}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead()}
                className="text-xs text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                {t('notifications.empty')}
              </div>
            ) : (
              notifications.map((notif) => (
                <NotificationItem
                  key={notif.id}
                  notification={notif}
                  onRead={markRead}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
